import { download, generateCsv } from 'export-to-csv';
import { mutate } from 'swr';

import { publicConfig } from '@public-config';

import {
    CompanyProfile,
    QrScan,
    QrScanAISummary,
    QrScanExportRow,
    QrScanPayload,
    QrScanSourceOptions,
    QrScanTypeOptions,
    SlackChannel,
} from 'models';
import { AIServiceHelpers } from 'helpers/AIServiceHelpers';
import { CsvFormat, CsvHelpers } from 'helpers/CsvHelpers';
import { ShowtimeHelpers } from 'helpers/ShowtimeHelpers';

import { ApiService } from 'services/ApiService';
import { DateService } from 'services/DateService';
import { InternalTrackingService } from 'services/InternalTrackingService';

const ShowtimeService = {
    createQrScan: async (payload: Partial<QrScan>, company: Pick<CompanyProfile, 'name' | 'internal'>) => {
        InternalTrackingService.track(
            'qrScan.create',
            {
                data: payload,
            },
            {
                notifySlack: true,
                includeAnonymous: true,
                channel: SlackChannel.LOGGING,
                details: ShowtimeHelpers.slackDetails({ company, replusNotified: Boolean(payload.event) }),
            },
        );

        const result = await ApiService.post<{
            doc: QrScanPayload;
        }>(`${publicConfig.urls.api}/qrScans`, payload, { throwError: true });

        await mutate(ShowtimeHelpers.swr.qrScans(), (data: QrScanPayload[] | undefined) => {
            return [result.doc, ...(data || [])];
        });

        return result.doc;
    },

    editQrScan: async (id: string, payload: Partial<QrScan>) => {
        InternalTrackingService.track('qrScan.edit', {
            id,
            data: payload,
        });

        const result = await ApiService.patch<{
            doc: QrScanPayload;
        }>(`${publicConfig.urls.api}/qrScans/${id}`, payload, { throwError: true });

        await mutate(ShowtimeHelpers.swr.qrScans());

        return result.doc;
    },

    deleteQrScan: async (id: string) => {
        InternalTrackingService.track('qrScan.delete', {
            id,
        });

        await ApiService.patch(`${publicConfig.urls.api}/qrScans/${id}`, {
            deletedAt: new Date().toISOString(),
        });

        await mutate(ShowtimeHelpers.swr.qrScans());
    },

    getQrScansByEmail: async (email: string, eventId?: string) => {
        const result = await ApiService.get<{ docs: QrScanPayload[] }>(
            `${publicConfig.urls.api}/qrScans?where[userInfo.email][equals]=${encodeURIComponent(email)}${eventId ? `&where[event][equals]=${eventId}` : ''}&pagination=false`,
        );

        return result.docs;
    },

    getQrScansByUser: async (userId: string, eventId?: string) => {
        const result = await ApiService.get<{ docs: QrScanPayload[] }>(
            `${publicConfig.urls.api}/qrScans?where[user][equals]=${userId}${eventId ? `&where[event][equals]=${eventId}` : ''}&pagination=false`,
        );

        return result.docs;
    },

    getQrScansByCompany: async ({ companyId }: { companyId: string }) => {
        const result = await ApiService.get<{ docs: QrScanPayload[] }>(
            `${publicConfig.urls.api}/qrScans?where[company][equals]=${companyId}&pagination=false`,
        );

        return result.docs;
    },

    downloadQrScansCsv: async (company: Pick<CompanyProfile, 'id' | 'slug'>, format: CsvFormat) => {
        InternalTrackingService.track('qrScan.exportCsv', { companyId: company.id });

        const rows = await ApiService.get<QrScanExportRow[]>(
            `${publicConfig.urls.api}/qrScans/export?companyId=${company.id}`,
            { throwError: true },
        );

        const csvConfig = CsvHelpers.config(format, {
            filename: `${company.slug ?? company.id}-qr-scans`,
            columnHeaders: [
                { key: 'scannedAt', displayLabel: 'Date' },
                { key: 'source', displayLabel: 'Source' },
                { key: 'type', displayLabel: 'Type' },
                { key: 'item', displayLabel: 'Item' },
                { key: 'event', displayLabel: 'Event' },
                { key: 'name', displayLabel: 'Name' },
                { key: 'job', displayLabel: 'Job title' },
                { key: 'company', displayLabel: 'Company' },
                { key: 'talkedTo', displayLabel: 'Talked to' },
            ],
            replaceUndefinedWith: '',
        });

        const csvRows = rows.map((row) => ({
            ...row,
            scannedAt: DateService.format(row.scannedAt, 'YYYY-MM-DD HH:mm'),
            source: QrScanSourceOptions.find(({ value }) => value === row.source)?.label ?? row.source,
            type: QrScanTypeOptions.find(({ value }) => value === row.type)?.label ?? row.type,
            talkedTo: row.talkedTo.join(', '),
        }));

        download(csvConfig)(generateCsv(csvConfig)(csvRows));
    },

    summarizeVisitorNotes: async ({ userId, email }: { userId?: string; email?: string }) => {
        InternalTrackingService.track('qrScan.summarizeVisitorNotes', {
            userId,
            email,
        });

        return AIServiceHelpers.handleStream(
            (params) =>
                ApiService.post(`${publicConfig.urls.api}/qrScans/summarizeVisitorNotes`, params, {
                    headers: { Accept: 'text/event-stream' },
                }),
            {
                userId,
                email,
            },
        );
    },

    summarizeQrScans: async ({ companyId }: { companyId: string }) => {
        InternalTrackingService.track('qrScan.summarizeQrScans', {
            companyId,
        });

        const result = await ApiService.post<QrScanAISummary>(`${publicConfig.urls.api}/qrScans/summarize`, {
            companyId,
        });

        return result;
    },
};

export { ShowtimeService };
